import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

const W = 380;
const H = 480;
const BIRD_X = 90;
const BIRD_R = 14;
const GRAVITY = 0.28;      // slower fall
const FLAP = -6.0;         // gentler jump
const PIPE_W = 62;
const PIPE_GAP = 210;      // wider gap
const PIPE_SPEED = 1.9;    // slower pipes
const PIPE_EVERY = 130;    // more time between pipes

const MODULE_SRCS = [
  '/images/configurator/exterior_VLM04.svg',
  '/images/configurator/exterior_VLM06.svg',
  '/images/configurator/exterior_VLM10.svg',
  '/images/configurator/exterior_VLM12.svg',
];

const BIRD_IMG_SRC = '/images/configurator/bird.png';

function drawBird(ctx, x, y, vy, birdImg) {
  const angle = Math.min(Math.max(vy * 3.5 * (Math.PI / 180), -0.45), 1.1);
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);

  // ── Body ──────────────────────────────────────────────────────────────
  ctx.fillStyle = '#f5c518';
  ctx.beginPath();
  ctx.ellipse(0, 0, BIRD_R, BIRD_R - 3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#d4a017';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // ── Wing ─────────────────────────────────────────────────────────────
  ctx.fillStyle = '#e6b800';
  ctx.beginPath();
  ctx.ellipse(-3, 4, 8, 4, -0.3, 0, Math.PI * 2);
  ctx.fill();

  // ── Beak ─────────────────────────────────────────────────────────────
  ctx.fillStyle = '#ff8800';
  ctx.beginPath();
  ctx.moveTo(BIRD_R - 2, -2);
  ctx.lineTo(BIRD_R + 7, 0);
  ctx.lineTo(BIRD_R - 2, 4);
  ctx.closePath();
  ctx.fill();

  // ── Head image (or fallback eye) ──────────────────────────────────────
  const headSize = (BIRD_R - 1) * 2;
  if (birdImg && birdImg.complete && birdImg.naturalWidth > 0) {
    ctx.drawImage(birdImg, 0, -headSize / 2 - 3, headSize, headSize);
  } else {
    // fallback eye
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(5, -4, 4.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#1a1a2e';
    ctx.beginPath();
    ctx.arc(6.5, -4, 2.2, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function drawPipe(ctx, pipe, imgs) {
  const { x, topH, moduleIdx } = pipe;
  const img = imgs[moduleIdx];
  const botY = topH + PIPE_GAP;
  const botH = H - 22 - botY; // 22px ground

  if (img && img.complete && img.naturalWidth > 0) {
    // ── Top locker tower (flipped upside‑down) ──────────────────────────
    ctx.save();
    // transform: flip y around the bottom edge of the top slot
    ctx.transform(1, 0, 0, -1, x, topH);
    ctx.drawImage(img, 0, 0, PIPE_W, topH);
    ctx.restore();

    // ── Bottom locker tower (normal orientation) ────────────────────────
    if (botH > 0) {
      ctx.drawImage(img, x, botY, PIPE_W, botH);
    }
  } else {
    // Fallback solid bars while images are loading
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(x, 0, PIPE_W, topH);
    ctx.fillRect(x, botY, PIPE_W, H - botY - 22);
  }
}

function drawBackground(ctx, frame) {
  // sky gradient
  const sky = ctx.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, '#4ab8e8');
  sky.addColorStop(0.75, '#c4e9f8');
  sky.addColorStop(1, '#d6f0fa');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, H);

  // parallax clouds
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  const clouds = [
    { bx: 50,  by: 55, r: 20 },
    { bx: 160, by: 40, r: 16 },
    { bx: 270, by: 68, r: 24 },
    { bx: 400, by: 48, r: 18 },
    { bx: 500, by: 80, r: 14 },
  ];
  for (const c of clouds) {
    const cx = ((c.bx - frame * 0.25) % (W + 100) + W + 100) % (W + 100) - 50;
    ctx.beginPath();
    ctx.arc(cx,               c.by,           c.r,       0, Math.PI * 2);
    ctx.arc(cx + c.r * 0.8,  c.by - c.r * 0.4, c.r * 0.7, 0, Math.PI * 2);
    ctx.arc(cx + c.r * 1.55, c.by,           c.r * 0.85, 0, Math.PI * 2);
    ctx.fill();
  }

  // ground stripe
  ctx.fillStyle = '#5a8a3a';
  ctx.fillRect(0, H - 30, W, 8);
  ctx.fillStyle = '#8B6914';
  ctx.fillRect(0, H - 22, W, 22);
}

function updateGame(g) {
  if (g.state !== 'playing') return;
  g.bird.vy += GRAVITY;
  g.bird.y += g.bird.vy;
  g.frame++;

  if (g.frame % PIPE_EVERY === 0) {
    const topH = 70 + Math.random() * (H - PIPE_GAP - 130);
    g.pipes.push({
      x: W + 10,
      topH,
      passed: false,
      moduleIdx: Math.floor(Math.random() * MODULE_SRCS.length),
    });
  }

  for (const p of g.pipes) p.x -= PIPE_SPEED;
  g.pipes = g.pipes.filter(p => p.x > -PIPE_W - 10);

  for (const p of g.pipes) {
    if (!p.passed && p.x + PIPE_W < BIRD_X) {
      p.passed = true;
      g.score++;
    }
  }

  // ceiling / floor
  if (g.bird.y - BIRD_R < 0 || g.bird.y + BIRD_R > H - 22) {
    g.state = 'dead';
    if (g.score > g.best) g.best = g.score;
    return;
  }

  // pipe collision (with small forgiveness margin of 3px)
  for (const p of g.pipes) {
    if (
      BIRD_X + BIRD_R - 3 > p.x &&
      BIRD_X - BIRD_R + 3 < p.x + PIPE_W &&
      (g.bird.y - BIRD_R + 3 < p.topH || g.bird.y + BIRD_R - 3 > p.topH + PIPE_GAP)
    ) {
      g.state = 'dead';
      if (g.score > g.best) g.best = g.score;
    }
  }
}

const FlappyBird = ({ isOpen, toggle }) => {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);
  const imgsRef   = useRef([]);           // preloaded module images
  const birdImgRef = useRef(null);        // bird.png
  const gRef      = useRef({
    state: 'idle',
    bird:  { y: H / 2, vy: 0 },
    pipes: [],
    frame: 0,
    score: 0,
    best:  0,
  });
  const [uiScore, setUiScore] = useState(0);

  // Preload locker images once on mount
  useEffect(() => {
    MODULE_SRCS.forEach((src, i) => {
      const img = new window.Image();
      img.src = src;
      imgsRef.current[i] = img;
    });
    const bImg = new window.Image();
    bImg.src = BIRD_IMG_SRC;
    birdImgRef.current = bImg;
  }, []);

  const startGame = useCallback(() => {
    const g = gRef.current;
    g.state = 'playing';
    g.bird  = { y: H / 2, vy: 0 };
    g.pipes = [];
    g.frame = 0;
    g.score = 0;
    setUiScore(0);
  }, []);

  const flap = useCallback(() => {
    const g = gRef.current;
    if (g.state === 'idle' || g.state === 'dead') {
      startGame();
    } else if (g.state === 'playing') {
      g.bird.vy = FLAP;
    }
  }, [startGame]);

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    if (!isOpen) return;

    const g       = gRef.current;
    g.state = 'idle';
    g.bird  = { y: H / 2, vy: 0 };
    g.pipes = [];
    g.frame = 0;
    g.score = 0;
    setUiScore(0);

    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width  = W + 'px';
    canvas.style.height = H + 'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const onKey = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault();
        flap();
      }
    };
    window.addEventListener('keydown', onKey);

    let prevScore = -1;

    const tick = () => {
      updateGame(g);

      drawBackground(ctx, g.frame);
      for (const p of g.pipes) drawPipe(ctx, p, imgsRef.current);
      drawBird(ctx, BIRD_X, g.bird.y, g.bird.vy, birdImgRef.current);

      // score HUD
      if (g.state !== 'idle') {
        ctx.textAlign   = 'center';
        ctx.font        = 'bold 34px Manrope, sans-serif';
        ctx.lineWidth   = 3;
        ctx.strokeStyle = '#0f172a';
        ctx.strokeText(g.score, W / 2, 52);
        ctx.fillStyle   = '#fff';
        ctx.fillText(g.score, W / 2, 52);
      }

      // overlay screens
      if (g.state === 'idle') {
        ctx.fillStyle = 'rgba(0,0,0,0.48)';
        ctx.fillRect(0, 0, W, H);

        ctx.textAlign = 'center';
        ctx.font      = 'bold 28px Manrope, sans-serif';
        ctx.fillStyle = '#ffe066';
        ctx.fillText('🐦 FLAPPY LOCKER', W / 2, H / 2 - 38);

        ctx.font      = '17px Manrope, sans-serif';
        ctx.fillStyle = '#f1f5f9';
        ctx.fillText('Click o [Espacio] para jugar', W / 2, H / 2 + 8);

        if (g.best > 0) {
          ctx.font      = '14px Manrope, sans-serif';
          ctx.fillStyle = '#93c5fd';
          ctx.fillText(`🏆 Mejor puntuación: ${g.best}`, W / 2, H / 2 + 42);
        }
      }

      if (g.state === 'dead') {
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(0, 0, W, H);

        ctx.textAlign = 'center';
        ctx.font      = 'bold 32px Manrope, sans-serif';
        ctx.fillStyle = '#f87171';
        ctx.fillText('¡GAME OVER!', W / 2, H / 2 - 58);

        ctx.font      = '20px Manrope, sans-serif';
        ctx.fillStyle = '#f1f5f9';
        ctx.fillText(`Puntuación: ${g.score}`, W / 2, H / 2 - 18);

        ctx.font      = '18px Manrope, sans-serif';
        ctx.fillStyle = '#ffe066';
        ctx.fillText(`🏆 Mejor: ${g.best}`, W / 2, H / 2 + 18);

        ctx.font      = '14px Manrope, sans-serif';
        ctx.fillStyle = '#93c5fd';
        ctx.fillText('Click o [Espacio] para reintentar', W / 2, H / 2 + 58);
      }

      if (prevScore !== g.score) {
        setUiScore(g.score);
        prevScore = g.score;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, flap]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered style={{ maxWidth: W + 'px', width: W + 'px' }}>
      <ModalHeader toggle={toggle} style={{ padding: '8px 12px' }}>
        <span>🐦 Flappy Locker</span>
        {uiScore > 0 && (
          <span style={{ fontSize: '13px', marginLeft: '12px', color: '#64748b', fontWeight: 400 }}>
            Puntos: {uiScore}
          </span>
        )}
      </ModalHeader>
      <ModalBody className="p-0" style={{ lineHeight: 0 }}>
        <canvas
          ref={canvasRef}
          onClick={flap}
          style={{ cursor: 'pointer', display: 'block', width: '100%' }}
        />
      </ModalBody>
    </Modal>
  );
};

export default FlappyBird;
